/**
 *  @class
 *  @function BackToTop
 */
if (!customElements.get('search-form')) {
  class SearchForm extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.is_drawer = this.dataset.drawer;
      this.drawer = this.closest('#Search-Drawer');
      this.form = this.querySelector('form');
      this.button = document.querySelectorAll('.thb-quick-search');
      this.input = this.querySelector('.search-field');
      this.defaultTab = this.querySelector('.side-panel-content--initial');
      this.predictiveSearchResults = this.is_drawer
        ? (this.querySelector('[data-search-drawer-predictive-results]') ||
            this.querySelector('.side-panel-content.side-panel-content--has-tabs.thb-predictive-search'))
        : this.querySelector('.thb-predictive-search');
      this.cc = this.querySelector('.searchform--click-capture');
      this.sideDefault = this.drawer?.querySelector('[data-search-drawer-side-default]');
      this.sideSectionsHost = this.drawer?.querySelector('[data-search-drawer-side-sections]');
      this.noResultsMessage = this.drawer?.querySelector('[data-search-drawer-no-results-msg]');

      this.setupEventListeners();
    }

    setupEventListeners() {
      if (!this.form || !this.input) {
        return;
      }

      this.form.addEventListener('submit', this.onFormSubmit.bind(this));

      this.input.addEventListener(
        'input',
        debounce((event) => {
          this.onChange(event);
        }, 300)
      );
      this.input.addEventListener(
        'focus',
        debounce((event) => {
          this.onChange(event);
        }, 300)
      );

      this.onQuickSearchClick = this.onQuickSearchClick.bind(this);
      document.addEventListener('click', this.onQuickSearchClick, true);

      this.button.forEach((item) => {
        item.addEventListener('click', this.onQuickSearchClick);
      });

      this.cc?.addEventListener('click', this.close.bind(this));

      document.addEventListener('keyup', (e) => {
        if (e.code && e.code.toUpperCase() === 'ESCAPE') {
          this.close();
        }
      });
    }

    onQuickSearchClick(event) {
      const trigger = event.target.closest('.thb-quick-search');
      if (!trigger || !this.is_drawer) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      document.body.classList.add('open-cc');
      this.drawer.classList.add('active');
      this.drawer.dispatchEvent(new CustomEvent('search-drawer:open', { bubbles: true }));

      if (typeof Flickity !== 'undefined') {
        const resizeDelays = [0, 100, 300, 600, 900];
        resizeDelays.forEach((delay) => {
          setTimeout(() => {
            this.drawer.querySelectorAll('.search-drawer-iconics-carousel, .search-drawer-novelty-carousel').forEach((slider) => {
              const flkty = Flickity.data(slider);
              if (!flkty) {
                return;
              }
              flkty.reloadCells();
              flkty.resize();
              flkty.updateSlides();
              const viewport = flkty.viewport;
              if (!viewport) {
                return;
              }
              let maxHeight = 0;
              flkty.cells.forEach((cell) => {
                if (cell.size && cell.size.outerHeight > maxHeight) {
                  maxHeight = cell.size.outerHeight;
                }
              });
              if (maxHeight > 0) {
                viewport.style.height = maxHeight + 'px';
                slider.style.setProperty('--search-drawer-carousel-height', maxHeight + 'px');
                const collection = slider.closest('.iconics-collections, .novelty-collections');
                if (collection) {
                  collection.style.minHeight = maxHeight + 48 + 'px';
                }
              }
            });
          }, delay);
        });
      }

      if (this.input) {
        setTimeout(() => {
          this.input.focus();
        }, 300);
      }

      return false;
    }

    getQuery() {
      return this.input ? this.input.value.trim() : '';
    }

    resetDrawerSearchUi(options) {
      const showNoResultsMessage = options && options.showNoResultsMessage;
      if (!this.is_drawer) {
        return;
      }
      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.innerHTML = '';
        this.predictiveSearchResults.classList.remove('active', 'loading');
      }
      if (this.defaultTab) {
        this.defaultTab.hidden = false;
        this.defaultTab.style.removeProperty('display');
      }
      if (this.sideDefault && this.sideSectionsHost) {
        this.sideSectionsHost.innerHTML = '';
        this.sideSectionsHost.hidden = true;
        this.sideDefault.hidden = false;
      }
      if (this.noResultsMessage) {
        this.noResultsMessage.hidden = !showNoResultsMessage;
      }
    }

    onChange() {
      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        if (this.is_drawer) {
          this.resetDrawerSearchUi();
        } else {
          this.close();
        }
        return;
      }

      if (this.is_drawer && this.dataset.liveSearch === 'false') {
        return;
      }

      if (!this.predictiveSearchResults) {
        return;
      }

      if (!this.is_drawer) {
        this.open();
      }

      this.predictiveSearchResults.classList.add('active');
      this.getSearchResults(searchTerm);
    }

    onFormSubmit(event) {
      if (!this.getQuery().length) {
        event.preventDefault();
      }
    }

    getSearchResults(searchTerm) {
      if (!this.predictiveSearchResults || (this.is_drawer && this.dataset.liveSearch === 'false')) {
        return;
      }

      const requestedTerm = searchTerm;
      this.predictiveSearchResults.classList.add('loading');

      const params = new URLSearchParams();
      params.set('q', searchTerm);
      params.set('resources[type]', 'product,article,page,collection');
      params.set('resources[limit]', '10');
      params.set('resources[options][fields]', 'title,product_type,vendor,variants.title,variants.sku');
      params.set('section_id', 'predictive-search');

      fetch(`${theme.routes.predictive_search_url}?${params.toString()}`)
        .then((response) => {
          this.predictiveSearchResults?.classList.remove('loading');
          if (!response.ok) {
            throw new Error(response.status);
          }
          return response.text();
        })
        .then((text) => {
          if (!this.predictiveSearchResults) {
            return;
          }
          if (this.getQuery() !== requestedTerm || !this.getQuery().length) {
            return;
          }
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const sectionEl =
            doc.querySelector('#shopify-section-predictive-search') ||
            doc.querySelector('[id^="shopify-section-"][id*="predictive-search"]');
          if (!sectionEl) {
            return;
          }
          this.renderSearchResults(sectionEl.innerHTML, requestedTerm);
        })
        .catch(() => {
          this.predictiveSearchResults?.classList.remove('loading');
        });
    }

    renderSearchResults(resultsMarkup, requestedTerm) {
      if (!this.predictiveSearchResults) {
        return;
      }
      if (requestedTerm !== undefined && (this.getQuery() !== requestedTerm || !this.getQuery().length)) {
        return;
      }

      const trimmed = (resultsMarkup || '').trim();
      const isDrawerZeroHits =
        this.is_drawer && (!trimmed || trimmed.includes('data-predictive-no-results'));

      if (isDrawerZeroHits) {
        this.resetDrawerSearchUi({ showNoResultsMessage: true });
        return;
      }

      if (this.is_drawer && this.noResultsMessage) {
        this.noResultsMessage.hidden = true;
      }

      this.predictiveSearchResults.innerHTML = resultsMarkup;
      if (this.is_drawer && this.sideDefault && this.sideSectionsHost) {
        const fragmentRoot = this.predictiveSearchResults.querySelector('[data-predictive-search-side]');
        this.sideSectionsHost.innerHTML = '';
        if (fragmentRoot) {
          const inner = fragmentRoot.querySelector('.search-drawer-sections');
          if (inner) {
            this.sideSectionsHost.appendChild(inner);
            this.sideDefault.hidden = true;
            this.sideSectionsHost.hidden = false;
          }
        }
      }
      const submitBtn = this.predictiveSearchResults.querySelector('#search-results-submit');
      if (submitBtn) {
        submitBtn.addEventListener('click', () => {
          this.form.submit();
        });
      }
    }

    close() {
      this.predictiveSearchResults?.setAttribute('inert', '');
      document.body.classList.remove('open-search');
    }

    open() {
      document.body.classList.add('open-search');
      this.predictiveSearchResults?.removeAttribute('inert');
    }
  }
  customElements.define('search-form', SearchForm);
}
